import { useSellerCarts } from "@/queries/cart";
import { cn } from "@repo/design-system/utils";
import { useCallback, useState } from "react";
import { CartSummaryStats } from "./CartSummaryStats";
import { SellerCartAccordion } from "./SellerCartAccordion";

export interface CartContentsProps {
  /** Additional CSS classes for the scrolling body. */
  className?: string;
  /**
   * Skip the summary stats row. For a caller that already shows the product
   * count and total itself (the drawer's header) - without this the two rows
   * repeat the same numbers back to back.
   */
  hideSummary?: boolean;
}

/**
 * The cart's body - summary stats, then one accordion per seller cart - with no
 * opinion about the surface holding it.
 *
 * Extracted so the desktop rail and the tablet/laptop slide-over show the same
 * cart rather than two implementations of it. Everything around the body
 * (heading, checkout action, whether to render at all when empty) differs
 * between the two and stays with each caller: the rail hides itself when the
 * cart is empty, while a drawer the buyer just opened has to say *something*.
 * See storefront#145 (P4).
 */
export function CartContents({ className, hideSummary }: CartContentsProps) {
  const [expandedCartId, setExpandedCartId] = useState<string | null>(null);

  const { data, isLoading } = useSellerCarts();

  const handleToggle = useCallback((cartId: string) => {
    setExpandedCartId((prev) => (prev === cartId ? null : cartId));
  }, []);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="h-6 w-6 animate-spin rounded-full border-2 border-gray-200 border-t-purple-600" />
      </div>
    );
  }

  if (!data || data.summary.total_items === 0) {
    return (
      <p className="px-4 py-8 text-center text-sm text-gray-500">
        Your cart is empty.
      </p>
    );
  }

  return (
    <div className={cn("min-w-0", className)}>
      {!hideSummary && (
        <div className="border-b border-gray-100 px-4 py-3">
          <CartSummaryStats summary={data.summary} />
        </div>
      )}
      <div className="px-4">
        {data.carts.map((sellerCart) => (
          <SellerCartAccordion
            key={sellerCart.cart_id}
            sellerCart={sellerCart}
            isExpanded={expandedCartId === sellerCart.cart_id}
            onToggle={() => handleToggle(sellerCart.cart_id)}
          />
        ))}
      </div>
    </div>
  );
}
